import { useEffect, useState } from 'react';
import {
  fetchCoachTodaySchedule,
  saveCoachTodayDay,
  saveCoachDailySettings,
  listBibleBooks,
  getBibleChapter,
  searchBible,
} from '../../../utils/spiritualApi';

export default function CoachTodaySchedule() {
  const [autoDaily, setAutoDaily] = useState(true);
  const [days, setDays] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [openDate, setOpenDate] = useState(null);
  const [books, setBooks] = useState([]);
  const [pickBook, setPickBook] = useState('');
  const [pickChapter, setPickChapter] = useState(1);
  const [chapterCount, setChapterCount] = useState(1);
  const [verses, setVerses] = useState([]);
  const [pickVerse, setPickVerse] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState([]);

  const load = async () => {
    const schedule = await fetchCoachTodaySchedule();
    setAutoDaily(schedule.auto_daily !== false);
    setDays((schedule.days || []).map((d) => ({
      date: d.date,
      custom_text: d.verse?.custom_text || '',
      reflection: d.verse?.reflection || '',
      verse_id: d.verse?.verse_id || '',
      published: d.verse?.published !== false && d.highlight?.published !== false,
      source: d.verse?.source || d.highlight?.source || '',
      study_id: d.highlight?.study_id || '',
      study_text: d.highlight?.study_text || '',
      devotional_plan_id: d.highlight?.devotional_plan_id || '',
      devotional_day_index: d.highlight?.devotional_day_index || '',
      devotional_text: d.highlight?.devotional_text || '',
    })));
  };

  useEffect(() => {
    load().catch(() => setMsg('No se pudo cargar el calendario de Hoy.'));
    listBibleBooks()
      .then((rows) => setBooks(Array.isArray(rows) ? rows : []))
      .catch(() => setBooks([]));
  }, []);

  const toggleAuto = async (checked) => {
    setAutoDaily(checked);
    setBusy(true);
    try {
      await saveCoachDailySettings({ auto_daily: checked });
      setMsg(checked ? 'Automático diario activado.' : 'Automático diario apagado.');
    } catch (e) {
      setMsg(e?.response?.data?.error || 'No se pudo guardar el automático.');
    } finally {
      setBusy(false);
    }
  };

  const patchDay = (date, field, value) => {
    setDays((prev) => prev.map((d) => (d.date === date ? { ...d, [field]: value } : d)));
  };

  const applyVerse = (date, verse) => {
    if (!date || !verse) return;
    setDays((prev) => prev.map((d) => (d.date === date ? {
      ...d,
      verse_id: verse.id,
      custom_text: verse.text,
    } : d)));
    setPickVerse(String(verse.id));
    setMsg(`Versículo cargado: ${verse.reference || ''}`);
  };

  const loadPickChapter = async (bookCode, chapterNum) => {
    if (!bookCode) {
      setVerses([]);
      setChapterCount(1);
      return;
    }
    try {
      const data = await getBibleChapter(bookCode, chapterNum, { log: false });
      setVerses(data?.verses || []);
      setChapterCount(data?.chapter_count || 1);
      setPickChapter(Number(data?.chapter_number || chapterNum));
      setPickVerse('');
    } catch {
      setVerses([]);
      setMsg('No se pudo cargar ese capítulo.');
    }
  };

  const onPickBook = async (code) => {
    setPickBook(code);
    setSearchHits([]);
    await loadPickChapter(code, 1);
  };

  const onPickChapter = async (n) => {
    await loadPickChapter(pickBook, Number(n));
  };

  const onPickVerseId = (date, verseId) => {
    const v = verses.find((row) => String(row.id) === String(verseId));
    if (!v) return;
    const book = books.find((b) => b.code === pickBook);
    applyVerse(date, {
      id: v.id,
      text: v.text,
      reference: `${book?.name || pickBook} ${pickChapter}:${v.verse_number}`,
    });
  };

  const onSearch = async (e) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    try {
      const rows = await searchBible(searchQ.trim());
      setSearchHits(Array.isArray(rows) ? rows.slice(0, 8) : []);
      if (!rows?.length) setMsg('Sin coincidencias en la RVR1960.');
    } catch {
      setSearchHits([]);
      setMsg('No se pudo buscar en la Biblia.');
    }
  };

  const save = async (day, published) => {
    setBusy(true);
    setMsg('');
    try {
      await saveCoachTodayDay(day.date, {
        custom_text: day.custom_text,
        reflection: day.reflection,
        verse_id: day.verse_id || null,
        study_text: day.study_text,
        devotional_text: day.devotional_text,
        published,
      });
      setMsg(published ? `Guardado ${day.date}` : `Oculto ${day.date}`);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || 'No se pudo guardar el día.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="spiritual-today-widget space-y-3">
      <h3 className="spiritual-today-widget__title">Programar Hoy</h3>
      <p className="spiritual-today-widget__body">
        Lo que guardes para una fecha es lo que ven tus usuarios ese día. El automático solo rellena el versículo si el día está vacío; no pisa lo manual.
      </p>
      <label className="spiritual-hub__check">
        <input
          type="checkbox"
          checked={autoDaily}
          disabled={busy}
          onChange={(e) => toggleAuto(e.target.checked)}
        />
        Automático a diario (versículo RVR1960). Estudio y devocional los escribes tú.
      </label>
      {msg ? <p className="spiritual-today-widget__body">{msg}</p> : null}
      <ul className="space-y-2">
        {days.map((day) => {
          const open = openDate === day.date;
          return (
            <li key={day.date} className="spiritual-hub__card">
              <button
                type="button"
                className="spiritual-hub__day-toggle"
                onClick={() => setOpenDate(open ? null : day.date)}
              >
                {day.date}
                {day.source ? ` · ${day.source === 'auto' ? 'automático' : 'manual'}` : ''}
              </button>
              {open ? (
                <div className="space-y-2 mt-2">
                  <p className="spiritual-today-widget__label">Versículo</p>
                  <div className="spiritual-hub__pick-row">
                    <select
                      className="spiritual-hub__input"
                      value={pickBook}
                      onChange={(e) => onPickBook(e.target.value)}
                    >
                      <option value="">Libro</option>
                      {books.map((b) => (
                        <option key={b.id} value={b.code}>{b.name}</option>
                      ))}
                    </select>
                    <select
                      className="spiritual-hub__input"
                      value={pickChapter}
                      disabled={!pickBook}
                      onChange={(e) => onPickChapter(e.target.value)}
                    >
                      {Array.from({ length: chapterCount }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>Cap. {n}</option>
                      ))}
                    </select>
                    <select
                      className="spiritual-hub__input"
                      value={pickVerse}
                      disabled={!verses.length}
                      onChange={(e) => onPickVerseId(day.date, e.target.value)}
                    >
                      <option value="">Versículo</option>
                      {verses.map((v) => (
                        <option key={v.id} value={v.id}>{v.verse_number}</option>
                      ))}
                    </select>
                  </div>
                  <form className="spiritual-hub__pick-row" onSubmit={onSearch}>
                    <input
                      className="spiritual-hub__input"
                      placeholder="O busca por palabras (RVR1960)"
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                    />
                    <button type="submit" className="btn-secondary text-sm">Buscar</button>
                  </form>
                  {searchHits.length ? (
                    <ul className="space-y-1">
                      {searchHits.map((hit) => (
                        <li key={hit.id}>
                          <button
                            type="button"
                            className="spiritual-hub__hit"
                            onClick={() => applyVerse(day.date, hit)}
                          >
                            <strong>{hit.reference}</strong>
                            {' '}
                            {hit.text}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <textarea
                    className="spiritual-hub__textarea"
                    rows={3}
                    placeholder="O escríbelo aquí (texto Reina-Valera)"
                    value={day.custom_text}
                    onChange={(e) => {
                      patchDay(day.date, 'custom_text', e.target.value);
                      if (!e.target.value) patchDay(day.date, 'verse_id', '');
                    }}
                  />
                  <textarea
                    className="spiritual-hub__textarea"
                    rows={2}
                    placeholder="Reflexión"
                    value={day.reflection}
                    onChange={(e) => patchDay(day.date, 'reflection', e.target.value)}
                  />
                  <label className="spiritual-today-widget__body block">
                    Estudio del día
                    <textarea
                      className="spiritual-hub__textarea mt-1"
                      rows={4}
                      placeholder="Escribe el estudio bíblico de este día"
                      value={day.study_text}
                      onChange={(e) => patchDay(day.date, 'study_text', e.target.value)}
                    />
                  </label>
                  <label className="spiritual-today-widget__body block">
                    Devocional
                    <textarea
                      className="spiritual-hub__textarea mt-1"
                      rows={4}
                      placeholder="Escribe el devocional de este día"
                      value={day.devotional_text}
                      onChange={(e) => patchDay(day.date, 'devotional_text', e.target.value)}
                    />
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => save(day, true)}>
                      Guardar y publicar
                    </button>
                    <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={() => save(day, false)}>
                      Ocultar este día
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
